import {AsyncChannel} from "./concurrency/AsyncChannel";
import {Lifetime} from "./Lifetime";
import {Logger} from "./Logger";


export type SWebSocket = {
    send: (message: string) => void,
    incoming: AsyncGenerator<string, void>,
}

export async function webSocket({url, lifetime = new Lifetime()}: {
    url: string,
    lifetime?: Lifetime,
}): Promise<SWebSocket> {
    const log = Logger('webSocket', {withId: true});
    log('connecting...', {url});
    const ws = new WebSocket(url);

    lifetime.onTermination(() => {
        log('Lifetime terminated, cleaning up WebSocket', {readyState: ws.readyState});
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        if (ws.readyState !== WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
            ws.close();
        }
    });

    return new Promise((resolve, reject) => {
        ws.onerror = () => {
            log('connection failed during initialization');
            reject(new Error('WebSocket connection failed'));
        };

        ws.onopen = () => {
            log('connection opened');
            const messageChannel = new AsyncChannel<string>(lifetime.nested());

            ws.onerror = message => {
                log('disconnected with error', message);
                messageChannel.abort(new Error('WebSocket disconnected with error'));
                lifetime.terminate();
            };

            ws.onmessage = event => {
                log('received message', {data: event.data});
                messageChannel.send(event.data);
            };

            resolve({
                send: message => {
                    if (ws.readyState === WebSocket.OPEN) {
                        log('sending message', {message});
                        ws.send(message)
                    } else {
                        log.warn('attempted to send message while WebSocket not open', {readyState: ws.readyState});
                    }
                },
                incoming: messageChannel.incoming(),
            });
        };


        ws.onclose = () => {
            log('connection closed');
            lifetime.terminate();
        };
    });
}